import React, { useState } from 'react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { Button } from 'components/ui/button';
import {
    User,
    Settings,
    Shield,
    LogOut,
    Heart,
    ExternalLink,
    ChevronDown,
    Sparkles,
    Monitor,
    Sun,
    Moon,
} from 'lucide-react';
import { cn } from 'utils/cn';
import { useCommunity } from 'context/community';
import LangService from 'services/LangService';
import { THEME_PREFERENCES } from 'services/themeMode';

const THEME_OPTIONS = {
    AUTO: { labelKey: 'appearanceAuto', fallback: 'Auto', Icon: Monitor },
    LIGHT: { labelKey: 'appearanceLight', fallback: 'Light', Icon: Sun },
    DARK: { labelKey: 'appearanceDark', fallback: 'Dark', Icon: Moon },
};

const ThemeSwitch = () => {
    const { currentUser, updateThemePreference } = useCommunity();
    const [error, setError] = useState('');
    const [pending, setPending] = useState(false);
    const profileLangMap = LangService.map?.pages?.Profile || {};
    const value = THEME_PREFERENCES.includes(currentUser?.themePreference)
        ? currentUser.themePreference
        : 'AUTO';

    if (typeof updateThemePreference !== 'function') {
        return null;
    }

    const onChange = async (next) => {
        if (pending || next === value) {
            return;
        }
        setError('');
        setPending(true);
        try {
            await updateThemePreference(next);
        } catch (saveError) {
            setError(profileLangMap.appearanceError || 'Could not save appearance. Try again.');
        } finally {
            setPending(false);
        }
    };

    return (
        <div className="px-1.5 pb-1">
            <div
                role="radiogroup"
                aria-label={profileLangMap.tabAppearance || 'Appearance'}
                className="flex items-center gap-0.5 rounded-md bg-muted p-0.5"
            >
                {THEME_PREFERENCES.map((preference) => {
                    const option = THEME_OPTIONS[preference];
                    const selected = value === preference;
                    const label = profileLangMap[option.labelKey] || option.fallback;
                    const Icon = option.Icon;
                    return (
                        <Button
                            key={preference}
                            type="button"
                            variant="ghost"
                            size="sm"
                            role="radio"
                            aria-checked={selected}
                            aria-label={label}
                            title={label}
                            disabled={pending}
                            onClick={() => onChange(preference)}
                            className={cn(
                                'h-8 flex-1 px-0 shadow-none',
                                selected
                                    ? 'bg-background text-foreground hover:bg-background'
                                    : 'text-muted-foreground hover:text-foreground'
                            )}
                        >
                            <Icon className="h-4 w-4" />
                        </Button>
                    );
                })}
            </div>
            {error ? (
                <p role="alert" className="px-1 pt-1 text-xs text-destructive">{error}</p>
            ) : null}
        </div>
    );
};

export const NavUserMenu = ({
    user = {
        name: 'Alex Morgan',
        email: 'alex.morgan@joinee.io',
        role: 'Community Member',
        avatar: null,
        initials: 'AM',
        isAdmin: true,
        slug: 'alex-morgan',
    },
    onNavigate,
    onLogout,
    showSupport = true,
    onSupport,
    className,
}) => {
    const [open, setOpen] = useState(false);
    const menu = LangService.map?.components?.header || {};

    return (
        <DropdownMenu open={open} onOpenChange={setOpen}>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    className={cn(
                        'group flex items-center gap-2 p-1 rounded-full text-left transition-all duration-150',
                        'hover:ring-2 hover:ring-primary/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        open && 'ring-2 ring-primary/40',
                        className
                    )}
                    aria-label="User account menu"
                >
                    <div className="relative">
                        {user.avatar ? (
                            <img
                                src={user.avatar}
                                alt={user.name}
                                className="w-9 h-9 rounded-full object-cover border border-border shadow-xs"
                            />
                        ) : (
                            <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center text-xs font-semibold text-slate-700 border border-border">
                                {user.initials || 'U'}
                            </div>
                        )}
                        <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-emerald-500 ring-2 ring-background" />
                    </div>
                </button>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="end" className="w-60 p-1.5 shadow-xl">
                {/* User info header */}
                <div className="px-3 py-2.5 bg-slate-50 rounded-md mb-1">
                    <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold text-foreground truncate">{user.name}</p>
                        {user.isAdmin && (
                            <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-blue-100 text-blue-800 rounded">
                                {menu.adminBadge || 'Admin'}
                            </span>
                        )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                </div>

                <ThemeSwitch />

                <DropdownMenuItem
                    onClick={() => onNavigate && onNavigate(`/u/${user.slug || 'me'}`)}
                    className="flex items-center gap-2.5 px-2.5 py-2 text-sm cursor-pointer"
                >
                    <User className="w-4 h-4 text-muted-foreground" />
                    <span>{menu.viewProfile || 'View Profile'}</span>
                </DropdownMenuItem>

                <DropdownMenuItem
                    onClick={() => onNavigate && onNavigate('/profile')}
                    className="flex items-center gap-2.5 px-2.5 py-2 text-sm cursor-pointer"
                >
                    <Settings className="w-4 h-4 text-muted-foreground" />
                    <span>{menu.settings || 'Settings & Preferences'}</span>
                </DropdownMenuItem>

                {user.isAdmin && (
                    <DropdownMenuItem
                        onClick={() => onNavigate && onNavigate('/admin')}
                        className="flex items-center gap-2.5 px-2.5 py-2 text-sm cursor-pointer text-indigo-600 font-medium"
                    >
                        <Shield className="w-4 h-4" />
                        <span>{menu.adminPanel || 'Community Admin Panel'}</span>
                    </DropdownMenuItem>
                )}

                {showSupport ? (
                    <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                            onClick={() => (onSupport ? onSupport() : onNavigate && onNavigate('/support'))}
                            className="flex items-center gap-2.5 px-2.5 py-2 text-sm cursor-pointer text-muted-foreground hover:text-foreground"
                        >
                            <Heart className="w-4 h-4 text-rose-500" />
                            <span>{menu.support || 'Support Community'}</span>
                        </DropdownMenuItem>
                    </>
                ) : null}

                <DropdownMenuSeparator />

                <DropdownMenuItem
                    onClick={() => onLogout && onLogout()}
                    className="flex items-center gap-2.5 px-2.5 py-2 text-sm cursor-pointer text-destructive focus:bg-destructive/10 focus:text-destructive"
                >
                    <LogOut className="w-4 h-4" />
                    <span>{menu.signOut || 'Sign Out'}</span>
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};
